// "To be reviewed": what didn't match or couldn't be read on import — fix it here in one click instead of in Smartsheet.
import { useEffect, useState } from 'react';
import { write } from '../../lib/api';
import { useApp } from '../../lib/app';
import { REVIEW_FIELD, statusLabel, type Item, type RegisterDef, type ReviewFlag } from '../../lib/registers';
import { Button, errText, useToast } from '../kit';
import { suggest, type Named, type Org } from './shared';

export function ReviewBanner({
  item,
  def,
  buildings,
  orgs,
  canEdit,
  onChanged,
}: {
  item: Item;
  def: RegisterDef;
  buildings: Named[];
  orgs: Org[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const flags = item.review ?? [];
  if (!flags.length) return null;
  return (
    <section className="review-box" aria-label="To be reviewed">
      <div className="review-head">
        <strong>To be reviewed</strong>
        <span className="muted small">
          {flags.length === 1 ? 'One thing' : `${flags.length} things`} didn’t match or couldn’t be read when this came in from Smartsheet. Fix it here — the flag clears itself.
        </span>
      </div>
      {flags.map((f) => (
        <FlagRow key={f.k} flag={f} item={item} def={def} buildings={buildings} orgs={orgs} canEdit={canEdit} onChanged={onChanged} />
      ))}
    </section>
  );
}

function FlagRow({ flag, item, def, buildings, orgs, canEdit, onChanged }: { flag: ReviewFlag; item: Item; def: RegisterDef; buildings: Named[]; orgs: Org[]; canEdit: boolean; onChanged: () => void }) {
  const { can } = useApp();
  const toast = useToast();
  const isB = flag.field === 'building';
  const isO = flag.field === 'directed' || flag.field === 'org';
  const pool: Named[] = isB ? buildings : orgs;
  const guess = flag.raw && (isB || isO) ? suggest(flag.raw, pool) : undefined;
  const [pick, setPick] = useState(guess?.id ?? '');
  // the lists load after the page: pre-select the suggestion once it is known
  useEffect(() => {
    if (guess?.id) setPick((p) => p || guess.id);
  }, [guess?.id]);
  const canAlias = isB ? can('buildings', 4) : can('orgs', 4);
  const [remember, setRemember] = useState(Boolean(flag.raw) && canAlias);
  useEffect(() => setRemember(Boolean(flag.raw) && canAlias), [flag.raw, canAlias]);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);

  const okKeys = [...new Set([...(item.review_ok ?? []), flag.k])];
  async function run(fn: (batch: string) => Promise<void>, msg: string) {
    setBusy(true);
    try {
      await fn(crypto.randomUUID());
      toast({ tone: 'ok', text: msg });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }
  const dismiss = () => run((batch) => write('items', 'update', { id: item.id, batch_id: batch, values: { review_ok: okKeys }, summary: `Marked as checked: ${REVIEW_FIELD[flag.field] ?? flag.field}` }).then(() => undefined), 'Marked as checked');

  async function fixLink(batch: string) {
    const target = pool.find((p) => p.id === pick);
    if (!target) return;
    if (remember && flag.raw) {
      // the database links every item waiting on this name (this one included)
      const table = isB ? 'buildings' : 'organisations';
      await write(table, 'update', { id: target.id, batch_id: batch, values: { aliases: [...new Set([...(target.aliases ?? []), flag.raw])] }, summary: `Added “${flag.raw}” as another name for ${target.name}` });
    }
    const values: Record<string, unknown> = { review_ok: okKeys };
    if (isB) values.building_ids = [...new Set([...item.building_ids, target.id])];
    else if (flag.field === 'directed') values.directed_to = [...new Set([...item.directed_to, target.id])];
    else values.org_id = target.id;
    await write('items', 'update', { id: item.id, batch_id: batch, values, summary: `Linked ${REVIEW_FIELD[flag.field]} “${flag.raw ?? ''}” to ${target.name}` });
  }

  const label = REVIEW_FIELD[flag.field] ?? flag.field;
  let what: string;
  if (flag.k === 'building:none') what = 'No building given';
  else if (flag.field === 'title') what = 'No topic';
  else if (flag.field === 'status') what = `Approval value “${flag.raw}” not recognised — status set to ${statusLabel(item.status, item.type)}`;
  else if (isB || isO) what = `“${flag.raw}” didn’t match any ${isB ? 'building' : 'organisation'}`;
  else what = `“${flag.raw}” couldn’t be read as a ${flag.field === 'amount' ? 'number' : 'date'}`;

  return (
    <div className="review-row">
      <div className="review-what">
        <span className="review-field">{label}</span>
        <span>{what}</span>
      </div>
      {canEdit ? (
        <div className="review-fix">
          {isB || isO ? (
            <>
              <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label={`Choose the ${isB ? 'building' : 'organisation'}`}>
                <option value="">Choose {isB ? 'building' : 'organisation'}…</option>
                {pool.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {guess?.id === p.id ? ' (suggested)' : ''}
                  </option>
                ))}
              </select>
              {flag.raw && canAlias ? (
                <label className="check small">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember “{flag.raw}” for next time
                </label>
              ) : null}
              <Button kind="primary" disabled={!pick || busy} onClick={() => void run(fixLink, 'Fixed')}>
                Fix
              </Button>
            </>
          ) : flag.field === 'status' ? (
            <>
              <select value={val} onChange={(e) => setVal(e.target.value)} aria-label="Status">
                <option value="">Keep {statusLabel(item.status, item.type)}</option>
                {def.statuses.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s, item.type)}
                  </option>
                ))}
              </select>
              <Button
                kind="primary"
                disabled={busy}
                onClick={() =>
                  void run(
                    (batch) => write('items', 'update', { id: item.id, batch_id: batch, values: { review_ok: okKeys, ...(val ? { status: val, stage: null } : {}) }, summary: 'Checked the approval value' }).then(() => undefined),
                    'Fixed',
                  )
                }
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <input
                type={flag.field === 'amount' ? 'number' : flag.field === 'title' ? 'text' : 'date'}
                step={flag.field === 'amount' ? '0.01' : undefined}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                aria-label={label}
                placeholder={flag.field === 'title' ? 'Topic' : undefined}
              />
              <Button
                kind="primary"
                disabled={!val || busy}
                onClick={() =>
                  void run(
                    (batch) =>
                      write('items', 'update', {
                        id: item.id,
                        batch_id: batch,
                        values: { [flag.field]: flag.field === 'amount' ? Number(val) : val },
                        summary: `Fixed ${label}`,
                      }).then(() => undefined),
                    'Fixed',
                  )
                }
              >
                Save
              </Button>
            </>
          )}
          <Button kind="ghost" disabled={busy} onClick={() => void dismiss()}>
            It’s fine — mark as checked
          </Button>
        </div>
      ) : null}
    </div>
  );
}
